import {
  ApprovalActionData,
  CreateDrawingCommentData,
  CreateDrawingCommentReplyData,
  CreateDrawingVersionData,
  DrawingBulkOperation,
  DrawingCategory,
  DrawingQuery,
  DrawingStats,
  DrawingUploadData,
  ProjectDrawing,
} from '../types';
import api from './api';
import configService from './configService';

class DrawingService {
  // Helper method to determine the correct API endpoint based on context
  private getDrawingsEndpoint(projectId?: string): string {
    if (projectId) {
      return `/projects/${projectId}/drawings`;
    }
    return '/drawings';
  }

  // Get drawings with advanced filtering
  async getDrawings(query: DrawingQuery = {}, projectId?: string) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}?${params.toString()}`);

      // Handle different response structures gracefully
      const responseData = response.data.data || response.data;

      return {
        success: true,
        drawings: (responseData.drawings || responseData || []) as ProjectDrawing[],
        total: responseData.pagination?.totalItems || responseData.total || 0,
        page: responseData.pagination?.currentPage || responseData.page || 1,
        totalPages: responseData.pagination?.totalPages || responseData.totalPages || 1,
        pagination: responseData.pagination,
      };
    } catch (error) {
      console.error('Failed to fetch drawings:', error);
      return {
        success: false,
        message: 'Failed to load drawings',
        drawings: [],
        total: 0,
        page: 1,
        totalPages: 1,
      };
    }
  }

  // Get projects for dropdown (dynamic)
  async getProjectsForDropdown() {
    try {
      const response = await api.get('/projects/dropdown');
      const responseData = response.data.data || response.data;
      return (responseData || []).map((project: any) => ({
        value: project.id,
        label: project.name,
      }));
    } catch (error) {
      console.error('Failed to fetch projects for dropdown:', error);
      return [];
    }
  }

  // Get drawing-related configurations using bulk API
  async getDrawingConfigurations() {
    try {
      const result = await configService.getBulkConfigurations([
        'drawing-categories',
        'drawing-statuses',
        'drawing-disciplines',
        'drawing-phases',
        'drawing-sort-options',
        'drawing-sheet-sizes',
        'drawing-visibility-options',
      ]);

      if (result.success && result.data) {
        const categories = (result.data['drawing-categories'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
          color: this.getCategoryColor(option.value),
        }));

        const statuses = (result.data['drawing-statuses'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
          color: this.getStatusColor(option.value),
        }));

        const disciplines = (result.data['drawing-disciplines'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
        }));

        const phases = (result.data['drawing-phases'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
        }));

        const sortOptions = (result.data['drawing-sort-options'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
        }));

        const sheetSizes = (result.data['drawing-sheet-sizes'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
        }));

        const visibilityOptions = (result.data['drawing-visibility-options'] || []).map(
          (option: any) => ({
            value: option.value,
            label: option.label,
          })
        );

        return {
          categories,
          statuses,
          disciplines,
          phases,
          sortOptions,
          sheetSizes,
          visibilityOptions,
        };
      }

      // Return default options if bulk fetch fails
      return {
        categories: this.getDefaultCategories(),
        statuses: this.getDefaultStatuses(),
        disciplines: this.getDefaultDisciplines(),
        phases: this.getDefaultPhases(),
        sortOptions: this.getDefaultSortOptions(),
        sheetSizes: this.getDefaultSheetSizes(),
        visibilityOptions: this.getDefaultVisibilityOptions(),
      };
    } catch (error) {
      console.error('Failed to fetch drawing configurations:', error);
      return {
        categories: this.getDefaultCategories(),
        statuses: this.getDefaultStatuses(),
        disciplines: this.getDefaultDisciplines(),
        phases: this.getDefaultPhases(),
        sortOptions: this.getDefaultSortOptions(),
        sheetSizes: this.getDefaultSheetSizes(),
        visibilityOptions: this.getDefaultVisibilityOptions(),
      };
    }
  }

  // Get drawing categories from configuration (dynamic)
  async getDrawingCategories() {
    const { categories } = await this.getDrawingConfigurations();
    return categories;
  }

  // Get drawing statuses from configuration
  async getDrawingStatuses() {
    const { statuses } = await this.getDrawingConfigurations();
    return statuses;
  }

  // Get drawing disciplines from configuration
  async getDrawingDisciplines() {
    const { disciplines } = await this.getDrawingConfigurations();
    return disciplines;
  }

  // Get drawing phases from configuration
  async getDrawingPhases() {
    const { phases } = await this.getDrawingConfigurations();
    return phases;
  }

  // Get sort options from configuration
  async getSortOptions() {
    const { sortOptions } = await this.getDrawingConfigurations();
    return sortOptions;
  }

  // Helper methods for default options
  private getDefaultCategories() {
    return [
      { value: 'floor_plan', label: 'Floor Plan', color: this.getCategoryColor('floor_plan') },
      { value: 'elevation', label: 'Elevation', color: this.getCategoryColor('elevation') },
      { value: 'section', label: 'Section', color: this.getCategoryColor('section') },
      { value: 'detail', label: 'Detail', color: this.getCategoryColor('detail') },
      { value: 'site_plan', label: 'Site Plan', color: this.getCategoryColor('site_plan') },
      { value: 'electrical', label: 'Electrical', color: this.getCategoryColor('electrical') },
      { value: 'plumbing', label: 'Plumbing', color: this.getCategoryColor('plumbing') },
      { value: 'hvac', label: 'HVAC', color: this.getCategoryColor('hvac') },
      { value: 'structural', label: 'Structural', color: this.getCategoryColor('structural') },
      { value: 'landscape', label: 'Landscape', color: this.getCategoryColor('landscape') },
      { value: 'fire_safety', label: 'Fire Safety', color: this.getCategoryColor('fire_safety') },
      { value: 'other', label: 'Other', color: this.getCategoryColor('other') },
    ];
  }

  private getDefaultStatuses() {
    return [
      { value: 'draft', label: 'Draft', color: this.getStatusColor('draft') },
      { value: 'under_review', label: 'Under Review', color: this.getStatusColor('under_review') },
      { value: 'approved', label: 'Approved', color: this.getStatusColor('approved') },
      { value: 'superseded', label: 'Superseded', color: this.getStatusColor('superseded') },
      { value: 'archived', label: 'Archived', color: this.getStatusColor('archived') },
    ];
  }

  private getDefaultDisciplines() {
    return [
      { value: 'architectural', label: 'Architectural' },
      { value: 'structural', label: 'Structural' },
      { value: 'mechanical', label: 'Mechanical' },
      { value: 'electrical', label: 'Electrical' },
      { value: 'plumbing', label: 'Plumbing' },
      { value: 'civil', label: 'Civil' },
      { value: 'landscape', label: 'Landscape' },
      { value: 'fire_safety', label: 'Fire Safety' },
      { value: 'other', label: 'Other' },
    ];
  }

  private getDefaultPhases() {
    return [
      { value: 'concept', label: 'Concept' },
      { value: 'design_development', label: 'Design Development' },
      { value: 'construction_documents', label: 'Construction Documents' },
      { value: 'as_built', label: 'As Built' },
      { value: 'revision', label: 'Revision' },
    ];
  }

  private getDefaultSortOptions() {
    return [
      { value: 'uploaded_at', label: 'Upload Date' },
      { value: 'name', label: 'Drawing Name' },
      { value: 'category', label: 'Category' },
      { value: 'status', label: 'Status' },
      { value: 'discipline', label: 'Discipline' },
      { value: 'phase', label: 'Phase' },
    ];
  }

  private getDefaultSheetSizes() {
    return [
      { value: 'A0', label: 'A0 (841×1189mm)' },
      { value: 'A1', label: 'A1 (594×841mm)' },
      { value: 'A2', label: 'A2 (420×594mm)' },
      { value: 'A3', label: 'A3 (297×420mm)' },
      { value: 'A4', label: 'A4 (210×297mm)' },
      { value: 'B0', label: 'B0 (1000×1414mm)' },
      { value: 'B1', label: 'B1 (707×1000mm)' },
      { value: 'B2', label: 'B2 (500×707mm)' },
      { value: 'B3', label: 'B3 (353×500mm)' },
      { value: 'B4', label: 'B4 (250×353mm)' },
      { value: 'CUSTOM', label: 'Custom Size' },
    ];
  }

  private getDefaultVisibilityOptions() {
    return [
      { value: 'public', label: 'Public' },
      { value: 'team_only', label: 'Team Only' },
      { value: 'admin_only', label: 'Admin Only' },
    ];
  }

  // Helper method to get category colors
  private getCategoryColor(category: string): string {
    const colorMap: Record<string, string> = {
      floor_plan: 'bg-red-100 text-red-800',
      elevation: 'bg-green-100 text-green-800',
      section: 'bg-yellow-100 text-yellow-800',
      detail: 'bg-purple-100 text-purple-800',
      site_plan: 'bg-indigo-100 text-indigo-800',
      electrical: 'bg-red-100 text-red-800',
      plumbing: 'bg-cyan-100 text-cyan-800',
      hvac: 'bg-orange-100 text-orange-800',
      structural: 'bg-gray-100 text-gray-800',
      landscape: 'bg-green-100 text-green-800',
      fire_safety: 'bg-red-100 text-red-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800';
  }

  // Helper method to get status colors
  private getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      superseded: 'bg-orange-100 text-orange-800',
      archived: 'bg-red-100 text-red-800',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Bulk operations
  async bulkDrawingOperation(operation: DrawingBulkOperation, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(`${endpoint}/bulk-operation`, operation);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to perform bulk operation:', error);
      return {
        success: false,
        message: 'Failed to perform bulk operation',
      };
    }
  }

  // Get single drawing by ID
  async getDrawing(drawingId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}`);
      return response.data.data as ProjectDrawing;
    } catch (error) {
      console.error('Failed to fetch drawing:', error);
      throw error;
    }
  }

  // Upload file and get reference
  async uploadFile(file: File, onProgress?: (progress: number) => void, projectId?: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('module', 'drawings'); // Specify module for S3 organization
      if (projectId) {
        formData.append('projectId', projectId);
      }

      const response = await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: progressEvent => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'File uploaded successfully',
      };
    } catch (error) {
      console.error('Failed to upload file:', error);
      return {
        success: false,
        message: 'Failed to upload file',
      };
    }
  }

  // Create drawing with file reference
  async createDrawing(uploadData: DrawingUploadData, fileReference: any, projectId?: string) {
    try {
      const drawingData = {
        project_id: uploadData.project_id,
        name: uploadData.name,
        description: uploadData.description,
        category: uploadData.category,
        status: uploadData.status,
        visibility: uploadData.visibility,
        location: uploadData.location,
        metadata: uploadData.metadata,
        tags: uploadData.tags,
        file_info: fileReference,
      };

      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(endpoint, drawingData);

      return {
        success: true,
        data: response.data.data as ProjectDrawing,
        message: response.data.message || 'Drawing created successfully',
      };
    } catch (error) {
      console.error('Failed to create drawing:', error);
      return {
        success: false,
        message: 'Failed to create drawing',
      };
    }
  }

  // Combined upload and create method (for convenience)
  async uploadDrawing(
    file: File,
    uploadData: DrawingUploadData,
    onProgress?: (progress: number) => void,
    projectId?: string
  ) {
    try {
      // Step 1: Upload file
      const fileUploadResult = await this.uploadFile(
        file,
        progress => {
          // First 50% of progress is file upload
          if (onProgress) onProgress(progress * 0.5);
        },
        projectId
      );

      if (!fileUploadResult.success) {
        return fileUploadResult;
      }

      // Step 2: Create drawing with file reference
      if (onProgress) onProgress(50); // File upload complete, now creating drawing

      const createResult = await this.createDrawing(uploadData, fileUploadResult.data, projectId);

      if (createResult.success && onProgress) {
        onProgress(100); // Complete
      }

      return createResult;
    } catch (error) {
      console.error('Failed to upload drawing:', error);
      return {
        success: false,
        message: 'Failed to upload drawing',
      };
    }
  }

  // Update drawing with optional file replacement
  async updateDrawing(
    drawingId: string,
    updateData: DrawingUploadData,
    file?: File | null,
    onProgress?: (progress: number) => void,
    projectId?: string
  ) {
    try {
      let fileReference;

      // If a new file is provided, upload it first
      if (file) {
        const fileUploadResult = await this.uploadFile(
          file,
          progress => {
            // First 50% of progress is file upload
            if (onProgress) onProgress(progress * 0.5);
          },
          projectId
        );

        if (!fileUploadResult.success) {
          return fileUploadResult;
        }

        fileReference = fileUploadResult.data;
        if (onProgress) onProgress(50); // File upload complete
      }

      // Prepare update data
      const updates = {
        name: updateData.name,
        description: updateData.description,
        category: updateData.category,
        status: updateData.status,
        visibility: updateData.visibility,
        location: updateData.location,
        metadata: updateData.metadata,
        tags: updateData.tags,
        ...(fileReference && { file_info: fileReference }),
      };

      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.put(`${endpoint}/${drawingId}`, updates);

      if (onProgress) onProgress(100); // Complete

      return {
        success: true,
        data: response.data.data as ProjectDrawing,
        message: response.data.message || 'Drawing updated successfully',
      };
    } catch (error) {
      console.error('Failed to update drawing:', error);
      return {
        success: false,
        message: 'Failed to update drawing',
      };
    }
  }

  // Update drawing metadata only (legacy method)
  async updateDrawingMetadata(
    drawingId: string,
    updates: Partial<ProjectDrawing>,
    projectId?: string
  ) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.put(`${endpoint}/${drawingId}`, updates);
      return {
        success: true,
        data: response.data.data as ProjectDrawing,
        message: response.data.message || 'Drawing updated successfully',
      };
    } catch (error) {
      console.error('Failed to update drawing:', error);
      throw error;
    }
  }

  // Delete drawing
  async deleteDrawing(drawingId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.delete(`${endpoint}/${drawingId}`);
      return {
        success: true,
        message: response.data.message || 'Drawing deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete drawing:', error);
      return {
        success: false,
        message: 'Failed to delete drawing',
      };
    }
  }

  // Download single drawing
  async downloadDrawing(drawingId: string, fileName: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/download`, {
        responseType: 'blob',
      });

      this.triggerDownload(response.data, fileName);
    } catch (error) {
      console.error('Failed to download drawing:', error);
      throw error;
    }
  }

  // Get drawing statistics
  async getDrawingStats(projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/stats`);

      // Handle different response structures gracefully
      const responseData = response.data.data || response.data;
      return responseData as DrawingStats;
    } catch (error) {
      console.error('Failed to fetch drawing stats:', error);
      // Return default stats structure instead of throwing
      return {
        total_drawings: 0,
        category_breakdown: [],
        status_breakdown: [],
        discipline_breakdown: [],
        recent_drawings: [],
      } as DrawingStats;
    }
  }

  // Get drawing version history
  async getVersionHistory(drawingId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/versions`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch version history:', error);
      throw error;
    }
  }

  // Search drawings by advanced criteria
  async searchDrawings(
    searchTerm: string,
    filters: Partial<DrawingQuery> = {},
    projectId?: string
  ) {
    try {
      const query = {
        search: searchTerm,
        ...filters,
      };
      return this.getDrawings(query, projectId);
    } catch (error) {
      console.error('Failed to search drawings:', error);
      throw error;
    }
  }

  // Helper method to trigger file download
  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Generate drawing ID for new drawing
  async generateDrawingId(projectId: string, category: DrawingCategory) {
    try {
      const response = await api.post('/drawings/generate-id', {
        project_id: projectId,
        category: category,
      });
      return response.data.drawing_id as string;
    } catch (error) {
      console.error('Failed to generate drawing ID:', error);
      throw error;
    }
  }

  // ============ DRAWING COMMENTS SYSTEM ============

  // Get comments for a drawing
  async getDrawingComments(drawingId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/comments`);
      return {
        success: true,
        data: response.data.data || response.data,
      };
    } catch (error) {
      console.error('Failed to fetch drawing comments:', error);
      return {
        success: false,
        message: 'Failed to fetch comments',
        data: [],
      };
    }
  }

  // Create a new comment on a drawing
  async createDrawingComment(commentData: CreateDrawingCommentData, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(
        `${endpoint}/${commentData.drawing_id}/comments`,
        commentData
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Comment added successfully',
      };
    } catch (error) {
      console.error('Failed to create comment:', error);
      return {
        success: false,
        message: 'Failed to add comment',
      };
    }
  }

  // Reply to a comment
  async replyToDrawingComment(replyData: CreateDrawingCommentReplyData, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(
        `${endpoint}/comments/${replyData.comment_id}/replies`,
        replyData
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Reply added successfully',
      };
    } catch (error) {
      console.error('Failed to reply to comment:', error);
      return {
        success: false,
        message: 'Failed to add reply',
      };
    }
  }

  // Resolve or unresolve a comment
  async toggleCommentResolution(commentId: string, resolved: boolean, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.put(`${endpoint}/comments/${commentId}/resolve`, { resolved });
      return {
        success: true,
        data: response.data.data,
        message:
          response.data.message || `Comment ${resolved ? 'resolved' : 'reopened'} successfully`,
      };
    } catch (error) {
      console.error('Failed to toggle comment resolution:', error);
      return {
        success: false,
        message: `Failed to ${resolved ? 'resolve' : 'reopen'} comment`,
      };
    }
  }

  // Delete a comment
  async deleteDrawingComment(commentId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.delete(`${endpoint}/comments/${commentId}`);
      return {
        success: true,
        message: response.data.message || 'Comment deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete comment:', error);
      return {
        success: false,
        message: 'Failed to delete comment',
      };
    }
  }

  // ============ DRAWING APPROVAL SYSTEM ============

  // Get approval workflow for a drawing
  async getDrawingApprovalWorkflow(drawingId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/approval`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to fetch approval workflow:', error);
      return {
        success: false,
        message: 'Failed to fetch approval workflow',
        data: null,
      };
    }
  }

  // Start approval workflow for a drawing
  async startDrawingApproval(drawingId: string, workflowType: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(`${endpoint}/${drawingId}/approval/start`, {
        workflow_type: workflowType,
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Approval workflow started',
      };
    } catch (error) {
      console.error('Failed to start approval workflow:', error);
      return {
        success: false,
        message: 'Failed to start approval workflow',
      };
    }
  }

  // Submit approval action (approve, request changes, reject)
  async submitApprovalAction(
    drawingId: string,
    actionData: ApprovalActionData,
    projectId?: string
  ) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(`${endpoint}/${drawingId}/approval/action`, actionData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Approval action submitted',
      };
    } catch (error) {
      console.error('Failed to submit approval action:', error);
      return {
        success: false,
        message: 'Failed to submit approval action',
      };
    }
  }

  // ============ DRAWING VERSION CONTROL ============

  // Get version history for a drawing
  async getDrawingVersionHistory(drawingId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/versions`);
      return {
        success: true,
        data: response.data.data || [],
      };
    } catch (error) {
      console.error('Failed to fetch version history:', error);
      return {
        success: false,
        message: 'Failed to fetch version history',
        data: [],
      };
    }
  }

  // Upload new version of a drawing
  async uploadDrawingVersion(
    versionData: CreateDrawingVersionData,
    onProgress?: (progress: number) => void,
    projectId?: string
  ) {
    try {
      // First upload the file
      const fileUploadResult = await this.uploadFile(
        versionData.file,
        progress => {
          if (onProgress) onProgress(progress * 0.7); // 70% for file upload
        },
        projectId
      );

      if (!fileUploadResult.success) {
        return fileUploadResult;
      }

      if (onProgress) onProgress(70); // File uploaded

      // Then create the version record
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(`${endpoint}/${versionData.drawing_id}/versions`, {
        version: versionData.version,
        revision: versionData.revision,
        change_notes: versionData.change_notes,
        file_info: fileUploadResult.data,
      });

      if (onProgress) onProgress(100); // Complete

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'New version uploaded successfully',
      };
    } catch (error) {
      console.error('Failed to upload drawing version:', error);
      return {
        success: false,
        message: 'Failed to upload new version',
      };
    }
  }

  // Revert to a specific version
  async revertToVersion(drawingId: string, versionId: string, projectId?: string) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.post(`${endpoint}/${drawingId}/versions/${versionId}/revert`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Reverted to previous version successfully',
      };
    } catch (error) {
      console.error('Failed to revert to version:', error);
      return {
        success: false,
        message: 'Failed to revert to previous version',
      };
    }
  }

  // Compare two versions of a drawing
  async compareDrawingVersions(
    drawingId: string,
    version1Id: string,
    version2Id: string,
    projectId?: string
  ) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/versions/compare`, {
        params: { version1: version1Id, version2: version2Id },
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to compare versions:', error);
      return {
        success: false,
        message: 'Failed to compare versions',
        data: null,
      };
    }
  }

  // Download a specific version
  async downloadVersion(
    drawingId: string,
    versionId: string,
    fileName: string,
    projectId?: string
  ) {
    try {
      const endpoint = this.getDrawingsEndpoint(projectId);
      const response = await api.get(`${endpoint}/${drawingId}/versions/${versionId}/download`, {
        responseType: 'blob',
      });

      this.triggerDownload(response.data, fileName);
    } catch (error) {
      console.error('Failed to download version:', error);
      throw error;
    }
  }
}

export default new DrawingService();
